import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PieChart from '../../../components/PieChart/PieChart';
import NoData from '../../../components/NoData/NoData';
import CommissionContractsStatisticsAPI from '../../../api/CommissionContractsStatisticsAPI';
import { inboundChannelToTranslationKeys } from '../../../shared/inboundChannelToTranslationKey';

type Props = { timeRange: { start: string; end: string }; compact?: boolean };

function CommissionContractsInboundChannels({ timeRange, compact = false }: Props) {
  const { t } = useTranslation();
  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const res = await CommissionContractsStatisticsAPI.getInboundChannels({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        if (!mounted) return;
        const rawLabels = res.data?.labels || [];
        const translatedLabels = rawLabels.map((label) => t(inboundChannelToTranslationKeys(label)));
        setLabels(translatedLabels);
        setValues(res.data?.values || []);
      } catch (error) {
        console.error('Failed to load commission contracts inbound channels', {
          startTime: timeRange.start,
          endTime: timeRange.end,
          error,
        });
        if (!mounted) return;
        setLabels([]);
        setValues([]);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [timeRange.start, timeRange.end, t]);

  return (
    <div className={compact ? undefined : 'space-y-2'}>
      {!compact && (
        <p className="text-xs text-muted-foreground">
          {t('stats.commission_contracts.inbound_channels_hint')}
        </p>
      )}
      {!labels.length ? (
        <NoData message={t('global.empty_results')} />
      ) : (
        <PieChart
          title={t('stats.commission_contracts.inbound_channels')}
          labels={labels}
          data={values}
          compact={compact}
        />
      )}
    </div>
  );
}

export default CommissionContractsInboundChannels;
